// Shared padding and label offsets for shoot time chart SVG layers.

export const THRESHOLD_PILL = {
  fontSize: 12,
  paddingX: 8,
  paddingY: 4,
  gapAbovePlot: 6,
  topMargin: 4,
} as const;

/** Invisible stroke width that makes the thin shoot-time curves hoverable. */
export const HOVER_HIT_WIDTH = 12;

/** Space reserved above the plot for editable threshold NumberInputs. */
export const THRESHOLD_EDIT_INPUT = {
  width: 88,
  height: 36,
  gapAbovePlot: 8,
} as const;

type ThresholdPillLayout = {
  halfWidth: number;
  height: number;
  borderRadius: number;
  offsetAbovePlotTop: number;
};

// Rough label width before SVG measurement (~0.6em per character at 12px).
export const estimateThresholdLabelWidth = (
  label: string,
  fontSize: number = THRESHOLD_PILL.fontSize
): number => label.length * fontSize * 0.6;

export const getThresholdPillLayout = (
  textWidth: number
): ThresholdPillLayout => {
  const { fontSize, paddingX, paddingY, gapAbovePlot } = THRESHOLD_PILL;
  const height = fontSize + paddingY * 2;
  const halfWidth = textWidth / 2 + paddingX;
  const borderRadius = Math.min(height * 0.23, 6);
  const offsetAbovePlotTop = height + gapAbovePlot;

  return {
    halfWidth,
    height,
    borderRadius,
    offsetAbovePlotTop,
  };
};

export const getThresholdPillTop = (
  plotTop: number,
  layout: ThresholdPillLayout
): number => plotTop - layout.offsetAbovePlotTop;

export const getThresholdPillCenterY = (
  plotTop: number,
  layout: ThresholdPillLayout
): number => getThresholdPillTop(plotTop, layout) + layout.height / 2;

const getShootTimeChartTopPadding = (): number => {
  const readOnlyOffset = getThresholdPillLayout(0).offsetAbovePlotTop;
  const editOffset =
    THRESHOLD_EDIT_INPUT.height + THRESHOLD_EDIT_INPUT.gapAbovePlot;
  return Math.max(readOnlyOffset, editOffset) + THRESHOLD_PILL.topMargin;
};

export const SHOOT_TIME_CHART_PADDING = {
  top: getShootTimeChartTopPadding(),
  right: 36,
  // Tick row, then the legend / axis-title row beneath it.
  bottom: 64,
  left: 56,
} as const;

const SHOOT_TIME_CHART_LAYOUT = {
  xAxisTickLabelGapBelowPlot: 16,
  xAxisLabelInsetFromBottom: 14,
  bandLabelPaddingFromPlotTop: 12,
  yAxisTickGapBeforePlot: 8,
  legendInsetFromLeft: 8,
} as const;

/** About five ticks. 7.5×10ⁿ supplies the 750 ms step on a 3000 ms domain. */
const NICE_STEP_MULTIPLIERS = [1, 1.5, 2, 2.5, 5, 7.5, 10] as const;
const TARGET_AXIS_TICK_COUNT = 5;

const roundTick = (value: number): number => {
  const rounded = Math.round(value * 1e6) / 1e6;
  return Object.is(rounded, -0) ? 0 : rounded;
};

/** Step size that yields about five ticks across `0…max`. */
export const getNiceStep = (
  max: number,
  targetTickCount: number = TARGET_AXIS_TICK_COUNT
): number => {
  if (!Number.isFinite(max) || max <= 0) {
    return 1;
  }
  const rough = max / Math.max(targetTickCount - 1, 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = NICE_STEP_MULTIPLIERS.map(
    (multiplier) => multiplier * magnitude
  ).find((candidate) => candidate >= rough - Number.EPSILON);
  return step ?? magnitude * 10;
};

/** Smallest nice multiple of the step that is at least `max`. */
export const getNiceCeiling = (
  max: number,
  targetTickCount: number = TARGET_AXIS_TICK_COUNT
): number => {
  if (!Number.isFinite(max) || max <= 0) {
    return 1;
  }
  const step = getNiceStep(max, targetTickCount);
  return roundTick(Math.ceil(max / step - Number.EPSILON) * step);
};

/**
 * Ticks from 0 through the end of the axis.
 * `ceil` lifts the last tick to a nice ceiling so the scale has a little headroom.
 */
export const getAxisTicks = (
  max: number,
  options?: { ceil?: boolean }
): number[] => {
  if (!Number.isFinite(max) || max <= 0) {
    return [0];
  }
  const step = getNiceStep(max);
  const end = options?.ceil ? getNiceCeiling(max) : max;
  const ticks: number[] = [];
  for (let index = 0; ; index++) {
    const tick = index * step;
    if (tick >= end - step * 0.01) {
      break;
    }
    ticks.push(roundTick(tick));
  }
  ticks.push(roundTick(end));
  return ticks;
};

export const getBandLabelY = (plotTop: number): number =>
  plotTop + SHOOT_TIME_CHART_LAYOUT.bandLabelPaddingFromPlotTop;

export type ShootTimeChartPadding = typeof SHOOT_TIME_CHART_PADDING;

export type ShootTimeXScale = (mm: number) => number;

export type ShootTimeYScale = (ms: number) => number;

export const getXAxisTickLabelY = (chartHeight: number): number =>
  chartHeight -
  SHOOT_TIME_CHART_PADDING.bottom +
  SHOOT_TIME_CHART_LAYOUT.xAxisTickLabelGapBelowPlot;

/** Shared baseline for the legend and the x-axis title. */
export const getChartFooterLabelY = (chartHeight: number): number =>
  chartHeight - SHOOT_TIME_CHART_LAYOUT.xAxisLabelInsetFromBottom;

export const getXAxisLabelPosition = (
  chartHeight: number,
  plotLeft: number,
  plotWidth: number
): { x: number; y: number } => ({
  x: plotLeft + plotWidth,
  y: getChartFooterLabelY(chartHeight),
});

export const getYAxisTickLabelX = (plotLeft: number): number =>
  plotLeft - SHOOT_TIME_CHART_LAYOUT.yAxisTickGapBeforePlot;

export const getLegendOriginX = (): number =>
  SHOOT_TIME_CHART_LAYOUT.legendInsetFromLeft;
